import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Wrench, Truck, ArrowLeftRight, Sparkles, Package } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const roleConfig = {
  service: { label: "Service Tech", icon: Truck, color: "#39FF14", path: "/tech-mobile" },
  shop: { label: "Shop", icon: Wrench, color: "#FF9500", path: "/shop" },
  detailer: { label: "Detailer", icon: Sparkles, color: "#A855F7", path: "/detailer" },
  inventory: { label: "Inventory", icon: Package, color: "#007AFF", path: "/inventory-dash" },
  dispatcher: { label: "Dispatcher", icon: null, color: "#007AFF", path: "/" },
  admin: { label: "Admin", icon: null, color: "#FF3B30", path: "/" },
};

function getDashboardRoles(user) {
  const roles = [];
  const primary = user?.role?.toLowerCase();
  const secondary = user?.secondary_roles || [];

  if (primary === "admin" || primary === "dispatcher") roles.push(primary);
  if (secondary.includes("service")) roles.push("service");
  if (secondary.includes("shop")) roles.push("shop");
  if (secondary.includes("detailer")) roles.push("detailer");
  if (secondary.includes("inventory")) roles.push("inventory");
  if (primary === "technician" && !roles.includes("service") && !roles.includes("shop") && !roles.includes("detailer")) {
    roles.push("service");
  }
  return roles;
}

export default function RoleSwitcher({ user, compact = false, excludeRoles = [] }) {
  const navigate = useNavigate();
  const location = useLocation();

  const dashboardRoles = getDashboardRoles(user);
  if (dashboardRoles.length < 2) return null;

  const getCurrentDashboard = () => {
    const path = location.pathname;
    if (path.startsWith("/shop")) return "shop";
    if (path.startsWith("/detailer")) return "detailer";
    if (path.startsWith("/inventory-dash")) return "inventory";
    if (path.startsWith("/tech")) return "service";
    return user?.role?.toLowerCase() === "admin" ? "admin" : "dispatcher";
  };

  const current = getCurrentDashboard();

  const available = dashboardRoles.filter((r) => {
    if (r === current) return false;
    if ((current === "admin" || current === "dispatcher") && (r === "admin" || r === "dispatcher")) return false;
    if (excludeRoles.includes(r)) return false;
    return true;
  });

  if (available.length === 0) return null;

  const handleSwitch = (role) => {
    const cfg = roleConfig[role];
    if (cfg) {
      const uid = user?.id || '';
      localStorage.setItem(`last_dashboard_${uid}`, cfg.path);
      navigate(cfg.path);
    }
  };

  // Compact: prominent single-target button
  if (compact && available.length === 1) {
    const target = available[0];
    const cfg = roleConfig[target];
    const Icon = cfg.icon || ArrowLeftRight;

    return (
      <button
        onClick={() => handleSwitch(target)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-all"
        style={{
          borderColor: `${cfg.color}60`,
          backgroundColor: `${cfg.color}15`,
        }}
        data-testid="role-switcher-compact"
      >
        <ArrowLeftRight className="h-4 w-4 text-white/80" />
        <span className="text-sm font-medium text-white/90">
          Switch to <span style={{ color: cfg.color }} className="font-bold">{cfg.label}</span>
        </span>
        <Icon className="h-4 w-4" style={{ color: cfg.color }} />
      </button>
    );
  }

  // Dropdown for 2+ targets
  const currentCfg = roleConfig[current] || roleConfig.dispatcher;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 transition-all"
          data-testid="role-switcher-dropdown"
        >
          <ArrowLeftRight className="h-4 w-4 text-white/80" />
          <span className="text-sm font-medium text-white/90">{currentCfg.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#121212] border-[#27272A] min-w-[200px]">
        {available.map((role) => {
          const cfg = roleConfig[role];
          const Icon = cfg.icon || ArrowLeftRight;
          return (
            <DropdownMenuItem
              key={role}
              onClick={() => handleSwitch(role)}
              className="flex items-center gap-3 cursor-pointer hover:bg-[#27272A] focus:bg-[#27272A] py-2"
              data-testid={`switch-to-${role}`}
            >
              <Icon className="h-4 w-4" style={{ color: cfg.color }} />
              <span className="text-white text-sm">
                Switch to <span style={{ color: cfg.color }} className="font-bold">{cfg.label}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
